import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  computed,
  Inject,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { ToolbarModule } from 'primeng/toolbar';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { Router, RouterOutlet } from '@angular/router';
import { SidenavComponent } from '../../components/sidenav/sidenav.component';
import { AuthService } from '../../services/auth.service';
import { LoginService } from '../../services/login.service';
import { isDevMode } from '@angular/core';

if (isDevMode()) {
  console.log('Modo de desenvolvimento');
} else {
 // console.log('Modo de produção');
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    RouterOutlet,
    SidenavComponent,
    CommonModule,
    ToolbarModule,
    ButtonModule,
    DrawerModule,
  ],
  template: `
    <div class="app-container">
      <!-- Barra superior que preenche toda a largura -->
      <p-toolbar styleClass="custom-toolbar fixed-toolbar">
        <ng-template pTemplate="start">
          <div class="flex items-center">
            <p-button
              (onClick)="toggleSidebar()"
              icon="pi pi-bars"
              styleClass="p-button-text p-button-plain menu-button"
              ariaLabel="Menu"
              title="Expandir/Contrair Menu">
            </p-button>
            <img src="/assets/amc-branca.png" width="150" height="50" alt="AMC Logo" />
            <div class="title">Autarquia Municipal de Trânsito e Cidadania</div>
          </div>
        </ng-template>
        <ng-template pTemplate="end">
          <div class="user">
            <div class="username">Usuário: </div>{{ shortName }}
            <p-button
              (onClick)="logout()"
              icon="pi pi-sign-out"
              styleClass="p-button-text p-button-plain"
              ariaLabel="Deslogar"
              title="Deslogar/Sair">
            </p-button>
          </div>
        </ng-template>
      </p-toolbar>

      <!-- Container principal com sidebar e conteúdo -->
      <div class="main-container">

        <!-- Sidebar desktop: sempre visível, apenas muda largura -->
        @if (!isMobile()) {
          <aside
            class="sidebar-desktop"
            [class.sidebar-collapsed]="collapsed()"
            [style.width]="sideNavWidth()"
          >
            <app-sidenav [collapsed]="collapsed()"></app-sidenav>
          </aside>
        }

        <!-- Sidebar mobile: overlay via p-drawer -->
        @if (isMobile()) {
          <p-drawer
            [visible]="sidebarVisible()"
            (visibleChange)="sidebarVisible.set($event)"
            position="left"
            [modal]="true"
            [dismissible]="true"
            [showCloseIcon]="true"
            [styleClass]="'sidebar-custom sidebar-below-toolbar'"
            [style]="{ width: '250px' }"
          >
            <app-sidenav [collapsed]="false"></app-sidenav>
          </p-drawer>
        }

        <div
          class="content"
          [style.margin-left]="isMobile() && sidebarVisible() ? sideNavWidth() : '0'"
        >
          <router-outlet></router-outlet>
        </div>
      </div>
    </div>`,
  styles: [
    `
      .app-container {
        height: 100vh;
        display: flex;
        flex-direction: column;
      }

      /* Barra superior fixa com largura completa */
      :host ::ng-deep .fixed-toolbar {
        position: fixed !important;
        top: 0;
        left: 0;
        right: 0;
        width: 100% !important;
        z-index: 1000;
        background-color: rgb(241, 90, 34) !important;
        color: white !important;
        height: 64px;
        min-height: 64px;
        padding: 0.5rem 1rem;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }

      :host ::ng-deep .fixed-toolbar .p-toolbar-group-start,
      :host ::ng-deep .fixed-toolbar .p-toolbar-group-end {
        color: white;
      }

      :host ::ng-deep .fixed-toolbar .p-button {
        color: white !important;
      }

      /* Ícone do menu sempre visível */
      :host ::ng-deep .menu-button {
        display: flex !important;
        visibility: visible !important;
      }

      /* Container principal abaixo da toolbar */
      .main-container {
        margin-top: 64px;
        height: calc(100vh - 64px);
        display: flex;
        position: relative;
      }

      /* Sidebar desktop fixa (sempre no layout) */
      .sidebar-desktop {
        flex-shrink: 0;
        height: 100%;
        overflow: hidden;
        background: #fff;
        border-right: 1px solid #e0e0e0;
        transition: width 500ms ease-in-out;
        box-shadow: 2px 0 4px rgba(0,0,0,0.05);
      }

      /* Sidebar posicionada abaixo da toolbar */
      :host ::ng-deep .sidebar-below-toolbar {
        top: 64px !important;
        height: calc(100vh - 64px) !important;
        transition: all 500ms ease-in-out;
      }

      :host ::ng-deep .sidebar-below-toolbar .p-drawer-content {
        padding: 0;
        height: 100%;
      }

      /* Área de conteúdo */
      .content {
        flex: 1;
        padding: 24px;
        transition: all 500ms ease-in-out;
        overflow-y: auto;
        height: calc(100vh - 64px);
      }

      .title {
        padding-left: 10px;
        color: white;
        font-weight: 600;
      }
      
      .flex {
        display: flex;
      }
      
      .items-center {
        align-items: center;
      }
      
      .user {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: white;
      }

      .username {
        color: white;
        font-weight: 500;
      }

      /* Responsivo para mobile */
      @media (max-width: 768px) {
        :host ::ng-deep .fixed-toolbar {
          padding: 0 8px;
        }
        
        .content {
          margin-left: 0 !important;
          padding: 16px;
        }
        
        .title {
          display: none;
        }
        
        .username {
          display: none;
        }
        
        .user {
          font-size: 14px;
        }

        /* Em mobile, sidebar modal não precisa ajuste de top */
        :host ::ng-deep .sidebar-below-toolbar.p-drawer-modal {
          top: 0 !important;
          height: 100vh !important;
        }
      }
    `,
  ],
})
export class HomeComponent implements OnDestroy {
  private resizeListener: () => void;

  authService = inject(AuthService);
  http = inject(HttpClient);
  loginService = inject(LoginService);

  isMobile = signal(false); // Novo signal para controle mobile
  isMenuOpen = signal(false); // Novo signal para estado do menu

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.checkScreenSize();
    this.resizeListener = () => this.checkScreenSize();
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeListener);
  }

  checkScreenSize() {
    const mobile = window.innerWidth < 768;
    this.isMobile.set(mobile);

    if (mobile) {
      this.collapsed.set(true);
      this.sidebarVisible.set(false); // Fecha o menu em mobile
      this.isMenuOpen.set(false);
    } else {
      this.sidebarVisible.set(true); // Sempre visível em desktop
      this.isMenuOpen.set(true);
      // Sidebar inicia collapsed (fechada) mas visível - ícone sempre visível
    }
  }

  toggleMenu() {
    if (this.isMobile()) {
      this.isMenuOpen.update((v) => !v); // Alterna estado do menu em mobile
    } else {
      this.collapsed.update((v) => !v); // Alterna colapso em desktop
    }
  }

  toggleSidebar() {
    if (this.isMobile()) {
      this.sidebarVisible.update((v) => !v); // Alterna visibilidade do sidebar em mobile
    } else {
      this.collapsed.update((v) => !v); // Alterna colapso em desktop
    }
  }

  shortName = this.loginService.getShortName();

  logout(): void {
    /*console.log('logout');*/
    sessionStorage.setItem('auth-token', '');
    sessionStorage.setItem('shortName', '');
    sessionStorage.clear();

    // Clear cookies manually
    this.document.cookie.split(';').forEach((cookie) => {
      const [name] = cookie.split('=');
      this.document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    this.authService.currentUserSig.set(null);
    this.router.navigateByUrl('login');
  }

  title = 'Repasses';
  collapsed = signal(true); // Inicia fechado (collapsed)
  sidebarVisible = signal(false); // Inicia visível em desktop
  sideNavWidth = computed(() => (this.collapsed() ? '65px' : '250px'));

  profilePicSize(): number {
    return 50;
  }
}
